// DM-8: units and the input grammar. Internal unit is mm.
// scalar := term (("+"|"-"|"*"|"/") term)* unit?   — left-to-right, no parens
// term   := number | feetInches;  unit applies to the WHOLE expression result.
// Decimal separator is "." only; "," is the pair separator (EC-DM-7).

export type Units = "mm" | "cm" | "m" | "in" | "ftin";

const WORD_UNITS: [string, number][] = [
  ["mm", 1],
  ["cm", 10],
  ["ft", 304.8],
  ["in", 25.4],
  ["m", 1000]
]; // order matters: longest prefixes first so "mm" wins over "m"

/** Factor for a bare number typed in document units. */
export function docUnitFactor(units: Units): number {
  switch (units) {
    case "mm":
      return 1;
    case "cm":
      return 10;
    case "m":
      return 1000;
    case "in":
      return 25.4;
    case "ftin":
      return 25.4; // bare numbers in ft-in documents mean inches
  }
}

interface Scanner {
  s: string;
  pos: number;
}

function scanNumber(sc: Scanner): number | null {
  const m = /^(\d+(?:\.\d*)?|\.\d+)/.exec(sc.s.slice(sc.pos));
  if (!m) return null;
  sc.pos += m[0].length;
  return Number(m[0]);
}

/** term := number | feetInches. Returns value + whether the term carried its own unit (ft-in). */
function scanTerm(sc: Scanner): { value: number; united: boolean } | null {
  const n = scanNumber(sc);
  if (n === null) return null;
  if (sc.s[sc.pos] === "'") {
    // feetInches := number "'" [number ["\""]] — but a trailing bare "'" at end of input is the ft unit,
    // which the expression-level unit handling deals with; here "'" mid-expression means feet.
    const save = sc.pos;
    sc.pos++;
    const inches = scanNumber(sc);
    if (inches !== null) {
      if (sc.s[sc.pos] === '"') sc.pos++;
      return { value: (n * 12 + inches) * 25.4, united: true };
    }
    // number followed by "'" with no inches: only ft-in if we're NOT at a spot where "'"
    // could be the whole-expression unit suffix (i.e. end of string). Treat end-of-string as unit.
    if (sc.pos >= sc.s.length) {
      sc.pos = save; // give the "'" back to the unit scanner
      return { value: n, united: false };
    }
    return { value: n * 12 * 25.4, united: true };
  }
  return { value: n, united: false };
}

function scanUnit(sc: Scanner): number | null {
  const rest = sc.s.slice(sc.pos);
  if (rest.startsWith('"')) {
    sc.pos += 1;
    return 25.4;
  }
  if (rest.startsWith("'")) {
    sc.pos += 1;
    return 304.8;
  }
  for (const [word, factor] of WORD_UNITS) {
    if (rest.startsWith(word)) {
      sc.pos += word.length;
      return factor;
    }
  }
  return null;
}

/**
 * Parse a scalar length expression to mm. Returns null on any failure
 * (EC-DM-5: caller flashes invalid). Negative results are the caller's problem
 * to validate per-field (EC-DM-6).
 */
export function parseScalar(input: string, units: Units): number | null {
  const sc: Scanner = { s: input.replace(/\s+/g, ""), pos: 0 };
  if (!sc.s) return null;

  let sign = 1;
  if (sc.s[0] === "-") {
    sign = -1;
    sc.pos = 1;
  }

  const first = scanTerm(sc);
  if (!first) return null;
  let value = sign * first.value;
  let united = first.united;

  while (sc.pos < sc.s.length && "+-*/".includes(sc.s[sc.pos]!)) {
    const op = sc.s[sc.pos]!;
    sc.pos++;
    const t = scanTerm(sc);
    if (!t) return null;
    united = united || t.united;
    switch (op) {
      case "+":
        value += t.value;
        break;
      case "-":
        value -= t.value;
        break;
      case "*":
        value *= t.value;
        break;
      case "/":
        if (Math.abs(t.value) < 1e-12) return null; // EC-DM-5 division by zero
        value /= t.value;
        break;
    }
  }

  const unit = scanUnit(sc);
  if (unit !== null) {
    if (united) return null; // ft-in term mixed with a trailing unit
    value *= unit;
    united = true;
  }

  if (sc.pos !== sc.s.length) return null;
  if (!united) value *= docUnitFactor(units);
  return Number.isFinite(value) ? value : null;
}

/** pair := scalar "," scalar (rect W,H etc.). */
export function parsePair(input: string, units: Units): [number, number] | null {
  const parts = input.split(",");
  if (parts.length !== 2) return null;
  const a = parseScalar(parts[0]!, units);
  const b = parseScalar(parts[1]!, units);
  return a !== null && b !== null ? [a, b] : null;
}

/** Line tool combined form: `length "<" angleDeg` (05 §TL-6). */
export function parseLengthAngle(
  input: string,
  units: Units
): { length: number; angleDeg: number | null } | null {
  const i = input.indexOf("<");
  if (i < 0) {
    const length = parseScalar(input, units);
    return length !== null ? { length, angleDeg: null } : null;
  }
  const length = parseScalar(input.slice(0, i), units);
  const angleDeg = parseAngle(input.slice(i + 1));
  return length !== null && angleDeg !== null ? { length, angleDeg } : null;
}

/** Angle fields take plain degrees, no unit (DM-8). */
export function parseAngle(input: string): number | null {
  const s = input.replace(/\s+/g, "");
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

export function parseCount(input: string, min: number, max: number): number | null {
  const s = input.trim();
  if (!/^\d+$/.test(s)) return null;
  const v = Number(s);
  return v >= min && v <= max ? v : null;
}

/** Format a mm value in document units to `precision` decimals (DM-8: round half away from zero). */
export function formatLength(mm: number, units: Units, precision: number): string {
  const round = (v: number, p: number): string => {
    const f = 10 ** p;
    const r = Math.sign(v) * Math.round(Math.abs(v) * f) / f;
    return r.toFixed(p);
  };
  switch (units) {
    case "mm":
      return round(mm, precision);
    case "cm":
      return round(mm / 10, precision);
    case "m":
      return round(mm / 1000, precision);
    case "in":
      return round(mm / 25.4, precision);
    case "ftin": {
      const totalIn = Math.abs(mm) / 25.4;
      const sign = mm < 0 ? "-" : "";
      const feet = Math.floor(totalIn / 12);
      const inches = totalIn - feet * 12;
      return `${sign}${feet}'-${round(inches, precision)}"`;
    }
  }
}

export function unitSuffix(units: Units): string {
  return units === "ftin" ? "" : units;
}
